import type { BuildDerived, BuildState } from '@/types';
import { trajectoryTable } from '@/lib/ballistics';
import { Card } from '@/components/ui/Card';
import { TrajectoryPlot } from './TrajectoryPlot';

interface TrajectoryTableProps {
  state: BuildState;
  derived: BuildDerived;
}

export function TrajectoryTable({ state, derived }: TrajectoryTableProps) {
  const points = trajectoryTable({
    muzzleFps: state.fps,
    weightGrains: derived.weight,
    bc: derived.bc,
  });

  return (
    <Card padding="lg">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Trajectory</h2>
        <span className="text-xs text-zinc-500 font-mono">BC {derived.bc.toFixed(2)} · level launch</span>
      </div>

      {/* Engineering-grade trajectory plot — visual signal above the table.
          Cyan dashed = flat-line reference (no drag, no drop). Ember solid =
          measured trajectory from the 4-body solver. Δ callout at 60 yd. */}
      <div className="mb-4 -mx-2 px-2 py-2 bg-zinc-950/40 rounded-md">
        <TrajectoryPlot
          points={points}
          bc={derived.bc}
          muzzleFps={state.fps}
          weightGr={derived.weight}
        />
      </div>

      <div className="overflow-x-auto scrollbar-thin -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-right text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="py-2 px-3 text-left">Range</th>
              <th className="py-2 px-3">Velocity</th>
              <th className="py-2 px-3">Time</th>
              <th className="py-2 px-3">Drop</th>
              <th className="py-2 px-3">KE</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p) => (
              <tr key={p.yards} className="border-b border-zinc-800/40">
                <td className="py-2 px-3 font-mono font-medium">{p.yards} yd</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{p.velocityFps.toFixed(0)} fps</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{p.timeSeconds.toFixed(3)} s</td>
                <td className="py-2 px-3 text-right font-mono tabular-nums text-ember-500 font-bold">
                  {p.dropInches.toFixed(1)}"
                </td>
                <td className="py-2 px-3 text-right font-mono tabular-nums">{p.energyFtLb.toFixed(1)} ft·lb</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-zinc-500 mt-3 leading-snug">
        Drop from a level launch (no sight elevation). Estimate; ±1" at 40 yd for typical hunting
        setups in still air. Add wind, sight height, and pin-gap modeling for production use.
      </p>
    </Card>
  );
}
